import React, { useEffect, useState } from 'react';
import { first, flowRight as compose, startCase } from 'lodash';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Avatar, Box, CircularProgress, Tooltip } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Close } from '@material-ui/icons';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as Sentry from '@sentry/browser';
import { colors as mhColors } from '@modernhealth/vital-foundation';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import { ProviderType } from 'src/constants';
import { pxToRem } from 'src/helpers/pxToRem';

import { memberClient } from '../../config/Apollo';
import { Dispatch, RootState } from '../../config/Store';
import { CurrentUser } from '../../rematch-models/user';
import { colors } from '../../themes/colors';
import { DAY_MONTH_YEAR_DATE_FORMAT, LONG_DATE_FORMAT } from '../../helpers/dateHelpers';
import { generateInitials } from '../../helpers/generateInitials';
import { getCurrentUser, getMyAssignmentWithMember } from '../../selectors/userSelectors';
import {
  GetMemberInformationQuery,
  useGetMemberInformationQuery,
} from './documents/GetMemberInformation.generated';
import { useGetMemberReportableSessionsCountQuery } from './documents/GetMemberReportableSessionsCount.generated';
import { useGetMyNotesQuery } from './documents/GetMyNotes.chat.generated';
import { PsychiatryVisitsCompletedNode, VisitsRemainingNode } from '../../generated/webapp_gql';
import ReportVisitDialog from '../Dialogs/ReportVisit';
import MHButton from '../MHButton';
import { AdditionalBenefitsSection, AdditionalBenefit } from './AdditionalBenefitsSection';
import { MemberSettingsNotes } from './MemberProfileNotes';
import MemberSettingsPanel from './MemberProfilePanel';
import MemberSettingsRow from './MemberProfileRow';
import { CareFormSection, IntakeForm } from './CareFormSection';
import SessionsInfoSection from './SessionsInfoSection/SessionsInfoSection';
import PsychiatrySessionsInfoSection from './PsychiatrySessionsInfoSection/PsychiatrySessionsInfoSection';
import CareTeamProviders, { CoachAssignment } from './CareTeamProviders';
import { InfoCard } from './InfoCard';
import { WarningIcon } from '../../assets/svgs/warning';
import { ReactComponent as IconQuestion } from '../../assets/svgs/icon-question.svg';
import { useIsNewInvoicingFlow } from '../NewInvoicing/hooks/useIsNewInvoicingFlow';

const styles = () =>
  createStyles({
    chatMemberSettings: {
      display: 'flex',
      flexDirection: 'column',
      width: 400,
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      backgroundColor: 'white',
      zIndex: 75, // the nav z-index is z-50 right now, the drawer should go above them
      borderLeft: `1px solid ${colors.gray1}`,
      overflowY: 'auto',
    },
    flexCenter: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    row1: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    settingsBody: {
      borderTop: `1px solid ${colors.gray1}`,
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    loading: {
      alignItems: 'center',
    },
    reportVisitButton: {
      backgroundColor: colors.blue6,
      width: '92%',
      display: 'block',
      margin: '15px auto 0',
      paddingTop: 10,
      paddingBottom: 10,
      marginBottom: 15,
    },
    reportVisitText: {
      fontFamily: 'Avenir-Heavy',
      color: 'white',
      fontSize: 16,
    },
    memberAvatar: {
      width: 70,
      height: 70,
    },
    benefitsWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: pxToRem(10, 16),
    },
    benefitsText: {
      fontSize: 15,
      fontWeight: 900,
      color: colors.black3,
    },
    benefitsRenewsText: {
      color: colors.gray11,
      fontSize: 12,
      fontWeight: 500,
    },
    careTeamInfoText: {
      fontWeight: 500,
      fontSize: 14,
      color: colors.gray5,
    },
    explanationText: {
      color: colors.gray5,
      fontWeight: 500,
      lineHeight: pxToRem(22),
      fontSize: 14,
      marginTop: 4,
      marginBottom: 6,
      fontFamily: 'Avenir-Medium',
    },
    warningBox: {
      backgroundColor: mhColors.orange10,
      width: '100%',
      padding: '1rem',
      display: 'flex',
      borderRadius: 5,
    },
    knowledgeCenterLink: {
      color: mhColors.orange85,
      textDecoration: 'underline',
      fontWeight: 'bold',
    },
    phq9ScoreRow: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.5rem',
    },
    rowTitleSpan: {
      paddingRight: '0.5rem',
    },
    subtitleSpan: {
      display: 'flex',
      alignItems: 'center',
    },
    moreInfoLink: {
      textDecoration: 'underline',
    },
    toolTipText: {
      fontSize: pxToRem(12),
      lineHeight: pxToRem(18),
    },
  });

export enum MemberProfileDrawerTestID {
  Container = 'MemberProfileDrawerTestID.Container',
  CloseButton = 'MemberProfileDrawerTestID.CloseButton',
  MemberName = 'MemberProfileDrawerTestID.MemberName',
  Phq9Warning = 'MemberProfileDrawerTestID.Phq9Warning',
}

type VisitsList = {
  key: string;
  extraKey?: string;
  value: string;
};

type Props = WithStyles<typeof styles> & {
  className?: string;
  disableReportVisit?: boolean;
  memberUuid: string;
  self: CurrentUser;
  onClickExit: () => void;
  navigateToIntakeFormDisplay?: boolean;
};

const getNewUserTopics = (
  showNewTopicsProvider: boolean,
  careRecommendation:
    | NonNullable<GetMemberInformationQuery['member']>['careRecommendations'][0]
    | undefined,
  improveRelationshipAndCommunicationFocusTopicUuid: string,
) => {
  let hasSelectedImproveRelationshipFocusTopic = false;

  const userNewTopics =
    showNewTopicsProvider && careRecommendation?.topicSelections
      ? careRecommendation.topicSelections.flatMap((topic) =>
          topic.subtopicSelections.map((subtopic) => {
            if (
              subtopic.subtopicOfFocus.uuid === improveRelationshipAndCommunicationFocusTopicUuid
            ) {
              hasSelectedImproveRelationshipFocusTopic = true;
            }
            return subtopic.subtopicOfFocus.name;
          }),
        )
      : null;

  return { userNewTopics, hasSelectedImproveRelationshipFocusTopic };
};

const SubtitleRowWithToolTip: React.FC<{
  subtitle: string;
  toolTipTitle: string | boolean | React.ReactChild | React.ReactFragment | React.ReactPortal;
  classes: Record<string, string>;
}> = ({ subtitle, toolTipTitle, classes }) => {
  return (
    <MemberSettingsRow
      subtitle={
        <span className={classes.subtitleSpan}>
          <span className={classes.rowTitleSpan}>{subtitle}</span>
          <Tooltip title={toolTipTitle} placement="top">
            <IconQuestion width={15} height={15} />
          </Tooltip>
        </span>
      }
    />
  );
};

const MemberProfileDrawer: React.FC<Props> = ({
  memberUuid,
  self,
  onClickExit,
  classes,
  className,
  disableReportVisit = false,
  navigateToIntakeFormDisplay = false,
}) => {
  const isNewInvoicingFlow = useIsNewInvoicingFlow();
  const history = useHistory();
  const [memberFetchFailed, setMemberFetchFailed] = useState(false);
  const [notesFetchFailed, setNotesFetchFailed] = useState(false);
  const [reportVisitModalOpen, setReportVisitModalOpen] = useState(false);
  const {
    data,
    loading: isGettingNotes,
    refetch: refetchNotes,
  } = useGetMyNotesQuery({
    variables: { memberUuid },
    onError: (err) => {
      setNotesFetchFailed(true);
      Sentry.captureException(err);
    },
    notifyOnNetworkStatusChange: true,
  });
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector(getCurrentUser);
  const providerIsPsychiatrist = currentUser.isPsychiatrist;
  const {
    hideVisitCount,
    hideReportVisit,
    showNewTopicsProvider,
    improveRelationshipAndCommunicationFocusTopicUuid,
    showMemberPhq9SelfharmScore,
  } = useFlags();
  const myAssignment = useSelector(getMyAssignmentWithMember(memberUuid));
  const dispatch = useDispatch<Dispatch>();
  const isProviderCoach = currentUser.providerType === ProviderType.COACH;

  const openReportVisitDialog = () => {
    setReportVisitModalOpen(true);
  };

  const closeReportVisitDialog = () => {
    setReportVisitModalOpen(false);
  };

  const { loading, data: memberData } = useGetMemberInformationQuery({
    client: memberClient,
    fetchPolicy: 'network-only',
    variables: { uuid: memberUuid },
  });

  const { data: memberReportableSessionsCountQueryResult } =
    useGetMemberReportableSessionsCountQuery({
      client: memberClient,
      fetchPolicy: 'network-only',
      variables: { coachUuid: self.uuid, memberUuid: memberUuid },
    });

  const forProviderType = () => {
    switch (currentUser.providerType) {
      case ProviderType.COACH:
        return {
          warningMessage:
            'This client indicated passive thoughts of death or hurting themselves on the PHQ-9 item 9. Modern Health sent an automatic email to the client to check if they’re receiving the care they need. To learn more about how you might support this client, visit the link below:',
          knowledgeCenterLink:
            'https://modernhealthprovider.zendesk.com/hc/en-us/articles/5473954481563',
          moreInfoUrl: 'https://modernhealthprovider.zendesk.com/hc/en-us/articles/5473954481563',
          buttonText: 'Recommended next steps',
        };
      default:
        return {
          warningMessage:
            'This client indicated passive thoughts of death or hurting themselves on the PHQ-9 item 9. Although Modern Health sent an automatic email to the client to check if they’re receiving the care they need, we recommend further follow-up with the client.',
          knowledgeCenterLink:
            'https://modernhealthprovider.zendesk.com/hc/en-us/articles/5473903256219',
          moreInfoUrl: 'https://modernhealthprovider.zendesk.com/hc/en-us/articles/5473903256219',
          buttonText: 'Learn more',
        };
    }
  };

  useEffect(() => {
    if (!loading && !memberData?.member) {
      setMemberFetchFailed(true);
    }
  }, [loading]);

  if (!myAssignment) {
    dispatch.partners.fetchPartners();
  }

  if (memberFetchFailed || notesFetchFailed) {
    return <div className={classes.chatMemberSettings}>Couldn't retrieve member information</div>;
  }

  const member = memberData?.member;

  if (!(member && member.company) || loading) {
    return (
      <div className={`${classes.chatMemberSettings} ${classes.flexCenter}`}>
        <CircularProgress size={50} />
      </div>
    );
  }
  const {
    careRecommendations,
    coachAssignments,
    company,
    companyAdditionalBenefits,
    visitsRemaining,
    surveys,
    psychiatryVisitsCompleted,
    phq9SelfharmScores,
  } = member;

  const formattedResetDate = company.resetDate
    ? moment(company.resetDate).format(DAY_MONTH_YEAR_DATE_FORMAT)
    : null;

  const isMinor: boolean = moment().diff(moment(member.birthDate), 'years') < 18;

  const careRecommendation = first(careRecommendations);

  const { userNewTopics, hasSelectedImproveRelationshipFocusTopic } = getNewUserTopics(
    showNewTopicsProvider,
    careRecommendation,
    improveRelationshipAndCommunicationFocusTopicUuid,
  );

  let visits: VisitsList[] = [];

  coachAssignments?.forEach((assignment) => {
    if (currentUser.uuid === assignment?.coach?.uuid) {
      visits =
        assignment?.coachingVisits
          .sort((a, b) => moment(b.scheduledTime).valueOf() - moment(a.scheduledTime).valueOf())
          .map((visit) => {
            return {
              key: moment(visit.scheduledTime).format(LONG_DATE_FORMAT),
              extraKey:
                assignment.coach?.providerType === ProviderType.PSYCHIATRIST
                  ? visit.isPsychiatryInitialEvaluation
                    ? 'Initial'
                    : 'Follow-up'
                  : undefined,
              value: startCase(visit.state.toLowerCase()),
            };
          }) || [];
    }
  });

  const goalInterests = member?.goalInterests?.map((goalInterest) => goalInterest) || [];

  const HandleOnReportVisitClick = (e) => {
    if (isNewInvoicingFlow) {
      if (memberReportableSessionsCountQueryResult?.memberReportableSessionsCount === 0) {
        enqueueSnackbar(
          `There are currently no valid sessions to report for ${member?.firstName} ${member?.lastName}`,
          {
            variant: 'error',
          },
        );
      } else {
        history.push(`/invoicing?memberUuid=${memberUuid}`);
      }
    } else {
      openReportVisitDialog();
    }
  };

  return (
    <div
      className={`${classes.chatMemberSettings} ${className}`}
      data-testid={MemberProfileDrawerTestID.Container}
    >
      <div className={classes.row1}>
        <IconButton onClick={onClickExit} data-testid={MemberProfileDrawerTestID.CloseButton}>
          <Close />
        </IconButton>
      </div>

      <Box display="flex" justifyContent="center" pb={1}>
        {member.avatar && <Avatar src={member.avatar} />}

        {!member.avatar && member.firstName && member.lastName && (
          <Avatar className={classes.memberAvatar}>
            {generateInitials([member.firstName, member.lastName])}
          </Avatar>
        )}
      </Box>

      <Box display="flex" justifyContent="center">
        <Typography variant="h5" data-testid={MemberProfileDrawerTestID.MemberName}>
          {member.firstName} {member.lastName}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" pb={0}>
        <Typography variant="subtitle2">
          Member since {moment(member.joinedAt).format(DAY_MONTH_YEAR_DATE_FORMAT)}
        </Typography>
      </Box>

      {showMemberPhq9SelfharmScore && !!phq9SelfharmScores && phq9SelfharmScores.length > 0 && (
        <Box padding={1} data-testid={MemberProfileDrawerTestID.Phq9Warning}>
          <Box className={classes.warningBox}>
            <Box marginRight={1}>
              <WarningIcon fill={mhColors.orange65} />
            </Box>
            <Box>
              <Typography>{forProviderType().warningMessage}</Typography>

              <Box paddingTop={2}>
                <a
                  className={classes.knowledgeCenterLink}
                  href={forProviderType().knowledgeCenterLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {forProviderType().buttonText}
                </a>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {careRecommendation?.recommendation === 'MENTAL_HEALTH_COACHING' && (
        <div className="self-center w-48 p-1 mt-1 mb-4 font-semibold text-center text-white bg-blue-400 rounded-full">
          Mental Health Coaching
        </div>
      )}

      <div className={classes.benefitsWrapper}>
        <Typography className={classes.benefitsText}>Benefits</Typography>

        <Typography className={classes.benefitsRenewsText}>Renews {formattedResetDate}</Typography>
      </div>

      <div className={classes.settingsBody}>
        {!hideVisitCount &&
          (providerIsPsychiatrist ? (
            <PsychiatrySessionsInfoSection
              visitsCompleted={psychiatryVisitsCompleted as PsychiatryVisitsCompletedNode}
            />
          ) : (
            <SessionsInfoSection
              insuranceStatus={member.insuranceStatus}
              isCompanyHpiEnabled={member.isCompanyHpiEnabled}
              visitsRemaining={visitsRemaining as VisitsRemainingNode}
            />
          ))}

        <CareFormSection
          directDisplay={!!navigateToIntakeFormDisplay}
          intakeForm={member?.intakeForm as IntakeForm}
        />

        <MemberSettingsPanel title="Account Details">
          {isMinor && <MemberSettingsRow title="Is Minor?" value="Yes" redValue />}
          <MemberSettingsRow title="Timezone" value={member.localTimezone} />

          <MemberSettingsRow
            title="Preferred Languages"
            value={member.languagePreferences.join(', ') || 'Not Specified'}
          />

          <MemberSettingsRow
            title="E-mail"
            value={member.newEmailAddressNotConfirmed || member.email}
          />

          <MemberSettingsRow title="Renewal Date" value={formattedResetDate} />
        </MemberSettingsPanel>

        <MemberSettingsPanel title="Care Team">
          <CareTeamProviders
            providerType={currentUser.providerType}
            coachAssignments={coachAssignments as CoachAssignment[]}
          />
        </MemberSettingsPanel>

        <MemberSettingsPanel title="Company Info">
          <MemberSettingsRow title="Name" value={company.name} />

          <MemberSettingsRow title="Additional Information" />

          <Typography>{company.specificInfo}</Typography>
        </MemberSettingsPanel>

        <MemberSettingsPanel title="Well-being Measures">
          <Box marginBottom={pxToRem(16)}>
            <SubtitleRowWithToolTip
              subtitle="WHO-5 Score"
              toolTipTitle={
                <span className={classes.toolTipText}>
                  WHO-5 scores range from 0 (minimal well-being) to 100 (maximal well-being).
                  Studies suggest that a score of 28 or below indicates possible symptoms of
                  depression or that this client may benefit from therapy services. However, this
                  determination cannot be made from the score alone and requires follow-up with your
                  client during your next session.
                </span>
              }
              classes={classes}
            />
            {surveys.length > 0 ? (
              <>
                {surveys.map(({ createdAt, score, id }) => (
                  <MemberSettingsRow
                    key={id}
                    title={moment(createdAt).format(LONG_DATE_FORMAT)}
                    value={score}
                  />
                ))}
              </>
            ) : (
              <Typography>Client has not completed measure</Typography>
            )}
          </Box>
          {showMemberPhq9SelfharmScore && (
            <>
              <SubtitleRowWithToolTip
                subtitle="PHQ-9 Item 9"
                toolTipTitle={
                  <span className={classes.toolTipText}>
                    The 9th item on the Patient Health Questionnaire (PHQ-9) asks about the
                    frequency of passive thoughts of death or self-harm within the last two weeks.
                    It is insufficient as a stand-alone measure for suicide risk and requires follow
                    up with the client.
                  </span>
                }
                classes={classes}
              />
              {member.phq9SelfharmScores && (
                <>
                  {member.phq9SelfharmScores?.length > 0 ? (
                    <>
                      {member.phq9SelfharmScores.map((score, i) => (
                        <MemberSettingsRow
                          key={score?.assessmentFlowId ?? i}
                          title={moment(score?.date).format(DAY_MONTH_YEAR_DATE_FORMAT)}
                          value={score?.text}
                        />
                      ))}
                    </>
                  ) : (
                    <Typography>Client has not completed measure</Typography>
                  )}
                </>
              )}
              <a
                className={classes.moreInfoLink}
                href={forProviderType().moreInfoUrl}
                target="_blank"
                rel="noreferrer"
              >
                Learn more about PHQ9
              </a>
            </>
          )}
        </MemberSettingsPanel>

        <AdditionalBenefitsSection
          benefits={companyAdditionalBenefits as AdditionalBenefit[]}
          companyName={company.name}
          firstName={member.firstName}
        />

        <MemberSettingsPanel title="Categories">
          {userNewTopics && userNewTopics.length > 0 && (
            <>
              <MemberSettingsRow subtitle="New Categories" />

              <span className={classes.explanationText}>
                New categories were collected for members who onboarded from January 2021 forward.
              </span>

              <MemberSettingsRow chips={userNewTopics} />

              {hasSelectedImproveRelationshipFocusTopic && (
                <InfoCard
                  title="Client request"
                  description={
                    <>
                      You're seeing this message because this client selected{' '}
                      <b>Relationships and Communication</b> during onboarding as the focus for
                      their coaching session. This is a pilot feature currently enabled for some
                      clients.
                    </>
                  }
                />
              )}
            </>
          )}

          <MemberSettingsRow subtitle="Old Categories" />

          <MemberSettingsRow chips={goalInterests} />
        </MemberSettingsPanel>

        <MemberSettingsPanel title="Sessions">
          {!isProviderCoach && !hideVisitCount && (
            <>
              <MemberSettingsRow
                subtitle="Psychiatry and medication management sessions completed"
                value={`${
                  (psychiatryVisitsCompleted?.initialEvaluationVisitsCompleted || 0) +
                  (psychiatryVisitsCompleted?.followUpVisitsCompleted || 0)
                }`}
              />
              <MemberSettingsRow
                compact
                title="Initial evaluation"
                value={`${psychiatryVisitsCompleted?.initialEvaluationVisitsCompleted || 0}`}
              />
              <MemberSettingsRow
                compact
                title="Follow-ups"
                value={`${psychiatryVisitsCompleted?.followUpVisitsCompleted || 0}`}
              />
              <MemberSettingsRow
                boldValue
                subtitle="Coaching sessions completed"
                value={`${
                  company.coveredCoachingVisits - (visitsRemaining?.coachingVisitsRemaining ?? 0)
                } of ${company.coveredCoachingVisits}`}
              />
              <MemberSettingsRow
                boldValue
                subtitle="Therapy sessions completed"
                value={`${
                  company.coveredTherapyVisits - (visitsRemaining?.therapyVisitsRemaining ?? 0)
                } of ${company.coveredTherapyVisits}`}
              />
            </>
          )}
          {!hideVisitCount && isProviderCoach && (
            <MemberSettingsRow
              boldValue
              subtitle="Coaching sessions completed"
              value={`${
                company.coveredCoachingVisits - (visitsRemaining?.coachingVisitsRemaining ?? 0)
              } of ${company.coveredCoachingVisits}`}
            />
          )}

          {hideVisitCount && (
            <>
              <MemberSettingsRow
                boldValue
                title="Coaching sessions total"
                value={`${company.coveredCoachingVisits}`}
              />

              <MemberSettingsRow
                boldValue
                title="Therapy sessions total"
                value={`${company.coveredTherapyVisits}`}
              />
            </>
          )}

          {visits.length > 0 && (
            <>
              <MemberSettingsRow subtitle="Sessions List" />
              <MemberSettingsRow compact list={visits} />
            </>
          )}
        </MemberSettingsPanel>

        <MemberSettingsPanel title="My Notes">
          <MemberSettingsNotes
            notes={data?.getMyNotes}
            isGettingNotes={isGettingNotes}
            member={member}
            refetchNotes={refetchNotes}
          />
        </MemberSettingsPanel>

        {!disableReportVisit && !hideReportVisit && myAssignment && (
          <MHButton
            data-testid="reportVisitButton"
            extraClasses={[classes.reportVisitButton]}
            onClick={HandleOnReportVisitClick}
          >
            <Typography className={classes.reportVisitText}>Report visit</Typography>
          </MHButton>
        )}
      </div>

      {!disableReportVisit && reportVisitModalOpen && myAssignment && (
        <ReportVisitDialog
          coachAssignment={myAssignment}
          onCancel={closeReportVisitDialog}
          onSuccess={closeReportVisitDialog}
          showSuccessModal
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  self: state.user.currentUser,
});

export default compose(withStyles(styles), connect(mapStateToProps))(MemberProfileDrawer);
